import React, {useEffect, useState} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import Icon from "../Utils/Icon";
import Button from "../Utils/UI/Button";
import StatusCheckbox from "../Utils/UI/StatusCheckbox";
import classes from "./styles.module.css";

const STATUS_GROUPS = {
    payInOnly: [
        "purchase", "purchaseDecline",
        "recurrent", "recurrentDecline", "recurrentCreated", "recurrentPending",
        "reversal", "reversalDecline", "reversalCreated", "reversalPending",
        "refund", "refundDecline", "refundCreated", "refundPending",
        "threeDSPending", "created"
    ],
    payOutOnly: ["payout", "payoutCreated", "payoutPending", "payoutDecline", "mobilePayout", "mobilePayoutCreated", "mobilePayoutPending", "mobilePayoutDecline"],
    p2pOnly: ["mP2P", "mP2PCreated", "mP2PPending", "mP2PDecline", "successfulP2P"],
    sbpOnly: ["sbp", "sbpCreated", "sbpPending", "sbpDecline", "successfulSBP"],
    qrOnly: ["qr", "qrCreated", "qrPending", "qrDecline"],
    any: ["purchaseCreated", "purchasePending", "chargeback"]
};

const ALL_STATUSES = [
    "mP2P", "mP2PCreated", "mP2PPending", "mP2PDecline", "successfulP2P",
    "sbp", "sbpCreated", "sbpPending", "sbpDecline", "successfulSBP",
    "qr", "qrCreated", "qrPending", "qrDecline",
    "purchase", "purchaseDecline",
    "recurrent", "recurrentDecline", "recurrentCreated", "recurrentPending",
    "reversal", "reversalDecline", "reversalCreated", "reversalPending",
    "refund", "refundDecline", "refundCreated", "refundPending",
    "threeDSPending",
    "payout", "payoutCreated", "payoutPending", "payoutDecline",
    "mobilePayout", "mobilePayoutCreated", "mobilePayoutPending", "mobilePayoutDecline",
    "purchaseCreated", "purchasePending", "chargeback", "created"
].sort();

const CATEGORY_NAMES = {
    payments: {id: "TransactionsFilters.category.payments", defaultMessage: "Платежи"},
    p2p: {id: "TransactionsFilters.category.p2p", defaultMessage: "P2P операции"},
    SBP: {id: "TransactionsFilters.category.SBP", defaultMessage: "СБП операции"},
    QR: {id: "TransactionsFilters.category.QR", defaultMessage: "QR операции"},
    payout: {id: "TransactionsFilters.category.payout", defaultMessage: "Выплаты"},
};

const CATEGORY_ICONS = {
    payments: "banknote",
    p2p: "coin-dollar",
    SBP: "mobile",
    QR: "mobile",
    payout: "dollar",
};

function TransactionsFilters({onApplyFilters, onSelectFilter, isLoading, token, adminPrivileges}) {
    const intl = useIntl();
    const [paymentsStatus, setPaymentsStatus] = useState({});
    const [topCategories, setTopCategories] = useState({
        payments: false,
        p2p: false,
        SBP: false,
        QR: false,
        payout: false,
    });

    const isStatusSelected = (statusName) => {
        if (!paymentsStatus[statusName]) return false;
        return paymentsStatus[statusName].selected === true;
    };

    useEffect(() => {
        const newTop = {...topCategories};

        if (topCategories.payments) {
            const allPayInSelected = STATUS_GROUPS.payInOnly.every(isStatusSelected);
            const anyAnySelected = STATUS_GROUPS.any.some(isStatusSelected);
            if (!allPayInSelected && !anyAnySelected) {
                newTop.payments = false;
            }
        }
        if (topCategories.p2p) {
            const allP2pSelected = STATUS_GROUPS.p2pOnly.every(isStatusSelected);
            const anyAnySelected = STATUS_GROUPS.any.some(isStatusSelected);
            if (!allP2pSelected && !anyAnySelected) newTop.p2p = false;
        }
        if (topCategories.SBP) {
            const allSbpSelected = STATUS_GROUPS.sbpOnly.every(isStatusSelected);
            const anyAnySelected = STATUS_GROUPS.any.some(isStatusSelected);
            if (!allSbpSelected && !anyAnySelected) newTop.SBP = false;
        }
        if (topCategories.QR) {
            const allQrSelected = STATUS_GROUPS.qrOnly.every(isStatusSelected);
            const anyAnySelected = STATUS_GROUPS.any.some(isStatusSelected);
            if (!allQrSelected && !anyAnySelected) newTop.QR = false;
        }
        if (topCategories.payout) {
            const allPayoutSelected = STATUS_GROUPS.payOutOnly.every(isStatusSelected);
            const anyAnySelected = STATUS_GROUPS.any.some(isStatusSelected);
            if (!allPayoutSelected && !anyAnySelected) newTop.payout = false;
        }

        setTopCategories(newTop);
    }, [paymentsStatus]);

    const handleTopCategoryChange = (category, checked) => {
        const updated = {...paymentsStatus};
        const newTop = {...topCategories, [category]: checked};

        STATUS_GROUPS.any.forEach(name => {
            const current = updated[name] || {};
            updated[name] = {...current, selected: checked};
        });

        if (category === "payments") {
            STATUS_GROUPS.payInOnly.forEach(name => {
                const current = updated[name] || {};
                updated[name] = {...current, selected: checked};
            });
        } else if (category === "payout") {
            STATUS_GROUPS.payOutOnly.forEach(name => {
                const current = updated[name] || {};
                updated[name] = {...current, selected: checked};
            });
        } else if (category === "p2p") {
            STATUS_GROUPS.p2pOnly.forEach(name => {
                const current = updated[name] || {};
                updated[name] = {...current, selected: checked};
            });
        } else if (category === "SBP") {
            STATUS_GROUPS.sbpOnly.forEach(name => {
                const current = updated[name] || {};
                updated[name] = {...current, selected: checked};
            });
        } else if (category === "QR") {
            STATUS_GROUPS.qrOnly.forEach(name => {
                const current = updated[name] || {};
                updated[name] = {...current, selected: checked};
            });
        }

        setPaymentsStatus(updated);
        setTopCategories(newTop);
        triggerOnSelectFilter(updated);
    };

    const handleStatusSelect = (statusName, checked) => {
        const current = paymentsStatus[statusName] || {};
        const updated = {
            ...paymentsStatus,
            [statusName]: {...current, selected: checked}
        };
        setPaymentsStatus(updated);
        triggerOnSelectFilter(updated);
    };

    function triggerOnSelectFilter(statusObj) {
        const selected = [];
        for (const key in statusObj) {
            if (statusObj[key] && statusObj[key].selected) {
                const item = {};
                item[key] = true;
                selected.push(item);
            }
        }
        onSelectFilter(selected);
    }

    return (
        <div className={classes.Card}>
            <div className={classes.Body}>

                <div className={classes.Group}>
                    <h6 className={classes.Title}>
                        <FormattedMessage id="TransactionsFilters.title.acquiring"
                                          defaultMessage="Эквайринг"/>
                    </h6>
                    <div className={classes.Column}>

                        <div className={classes.CheckboxWrap}>
                            <input id="aCard" type="checkbox"
                                   onChange={e => onSelectFilter({aCard: e.target.checked})}/>
                            <label htmlFor="aCard" className={classes.CustomCheckbox}>
                                <Icon className={classes.InputIcon} icon="credit-card" size="17px"/>
                                <span>
                                    <FormattedMessage id="TransactionsFilters.acquiring.card"
                                                      defaultMessage="Банковские карты"/>
                                </span>
                            </label>
                        </div>

                        <div className={classes.CheckboxWrap}>
                            <input id="aPhone" type="checkbox"
                                   onChange={e => onSelectFilter({aPhone: e.target.checked})}/>
                            <label htmlFor="aPhone" className={classes.CustomCheckbox}>
                                <Icon className={classes.InputIcon} icon="mobile" size="17px"/>
                                <span>
                                    <FormattedMessage id="TransactionsFilters.acquiring.phone"
                                                      defaultMessage="Мобильные телефоны"/>
                                </span>
                            </label>
                        </div>
                    </div>
                </div>

                <div className={classes.Group}>
                    <h6 className={classes.Title}>
                        <FormattedMessage id="TransactionsFilters.title.filters"
                                          defaultMessage="Фильтры"/>
                    </h6>
                    <div className={classes.Column}>
                        <div className={classes.CheckboxWrap}>
                            <input id="onlyRegistry" type="checkbox"
                                   onChange={e => onSelectFilter({onlyRegistry: e.target.checked})}/>
                            <label htmlFor="onlyRegistry" className={classes.CustomCheckbox}>
                                <Icon className={classes.InputIcon} icon="share" size="17px"/>
                                <span>
                                    <FormattedMessage id="TransactionsFilters.filters.registry"
                                                      defaultMessage="Выплаты по реестрам"/>
                                </span>
                            </label>
                        </div>
                        {["payments", "p2p", "SBP", "QR", "payout"].map(cat => (
                            <div key={cat} className={classes.CheckboxWrap}>
                                <input
                                    id={`top-${cat}`}
                                    type="checkbox"
                                    checked={topCategories[cat] === true}
                                    onChange={e => handleTopCategoryChange(cat, e.target.checked)}
                                />
                                <label htmlFor={`top-${cat}`} className={classes.CustomCheckbox}>
                                    <Icon className={classes.InputIcon} icon={CATEGORY_ICONS[cat]} size="17px"/>
                                    <span>{intl.formatMessage(CATEGORY_NAMES[cat])}</span>
                                </label>
                            </div>
                        ))}
                    </div>
                </div>

                <div className={classes.Group}>
                    <h6 className={classes.Title}>
                        <FormattedMessage id="TransactionsFilters.title.statuses" defaultMessage="Статусы"/>
                    </h6>
                    <div className={classes.Column}>
                        {ALL_STATUSES.map(statusName => {
                            const statusData = paymentsStatus[statusName];
                            const selected = statusData ? statusData.selected === true : false;
                            const count = statusData ? statusData.count || 0 : 0;

                            if (!adminPrivileges && count === 0) return null;

                            return (
                                <StatusCheckbox
                                    key={statusName}
                                    category=""
                                    name={statusName}
                                    count={count}
                                    isSelected={selected}
                                    isDisabled={false}
                                    onChange={obj => {
                                        const value = Object.values(obj)[0];
                                        handleStatusSelect(statusName, value);
                                    }}
                                />
                            );
                        })}
                    </div>
                </div>

            </div>

            <div className={classes.Footer}>
                <Button
                    onClick={onApplyFilters}
                    label="TransactionsFilters.btn.setFilters"
                    icon="filter"
                    iconSize="17px"
                    isLoading={!isLoading}
                />
            </div>
        </div>
    );
}

export default TransactionsFilters;



// WEBPACK FOOTER //
// ./src/Components/TransactionsFilters/index.js